import React from "react";
import { ShoppingCart, Truck, Tag, DollarSign } from "lucide-react";

export default function OrderSummary({
  cart,
  freight,
  discountValue,
  couponData,
  handleSendOrder,
  isOrderLoading,
  isFormValid = true, // 🎯 Nova prop: validação do formulário
  pendingReason = "", // motivo que impede finalizar (vem do CheckoutPage)
  isProcessing = false, // 🎯 Nova prop: estado de processamento
}) {
  // Calcula subtotal
  const subtotal = cart.reduce((acc, item) => {
    const price = Number(item.product_price || 0);
    const qty = Number(item.quantity || 1);
    return acc + price * qty;
  }, 0);

  // Calcula total
  const freightValue = Number(freight || 0);
  const discount = Number(discountValue || 0);
  const total = subtotal + freightValue - discount;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:sticky lg:top-6">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
        <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-brand-primary" />
        Resumo do Pedido
      </h2>

      {/* Lista de produtos */}
      <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
        {cart.map((item, idx) => {
          const itemPrice = item.product_price || 0;
          const itemQty = item.quantity || 1;
          const itemTotal = itemPrice * itemQty;

          return (
            <div key={idx} className="flex justify-between items-start text-sm">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{item.product_name}</p>
                {item.selected_produtos &&
                  item.selected_produtos.length > 0 && (
                    <ul className="text-xs text-gray-500 mt-1 space-y-0.5">
                      {item.selected_produtos.map((prod, prodIdx) => (
                        <li key={prodIdx}>
                          • {prod.name} x{prod.qty || 1}
                        </li>
                      ))}
                    </ul>
                  )}
                {item.selected_ingredients &&
                  item.selected_ingredients.length > 0 && (
                    <ul className="text-xs text-gray-500 mt-1 space-y-0.5">
                      {item.selected_ingredients.map((ing, ingIdx) => (
                        <li key={ingIdx}>• {ing.name}</li>
                      ))}
                    </ul>
                  )}
                <p className="text-xs text-gray-500 mt-1">
                  Qtd: {itemQty} × R$ {itemPrice.toFixed(2)}
                </p>
              </div>
              <p className="font-semibold text-gray-900">
                R$ {itemTotal.toFixed(2)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="border-t pt-4 space-y-2">
        {/* Subtotal */}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span className="font-medium text-gray-900">
            R$ {subtotal.toFixed(2)}
          </span>
        </div>

        {/* Frete */}
        {freight !== null && freight > 0 && (
          <div className="flex justify-between text-sm items-center">
            <span className="text-gray-600 flex items-center gap-1">
              <Truck className="w-4 h-4" />
              Entrega
            </span>
            <span className="font-medium text-gray-900">
              R$ {freight.toFixed(2)}
            </span>
          </div>
        )}

        {/* Desconto */}
        {discountValue > 0 && (
          <div className="flex justify-between text-sm items-center text-green-600">
            <span className="flex items-center gap-1">
              <Tag className="w-4 h-4" />
              Desconto {couponData?.code && `(${couponData.code})`}
            </span>
            <span className="font-medium">-R$ {discountValue.toFixed(2)}</span>
          </div>
        )}

        {/* Total */}
        <div className="border-t pt-2 mt-2">
          <div className="flex justify-between items-center">
            <span className="text-base sm:text-lg font-bold text-gray-900">
              Total
            </span>
            <span className="text-xl sm:text-2xl font-bold text-brand-primary">
              R$ {total.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* 🎯 Alerta quando formulário está inválido */}
      {!isFormValid && !isProcessing && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-xs sm:text-sm font-medium text-amber-800">
                {pendingReason ||
                  "Preencha todos os campos obrigatórios para finalizar seu pedido"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Botão Finalizar */}
      <button
        onClick={handleSendOrder}
        disabled={!isFormValid || isProcessing || isOrderLoading}
        className={`w-full mt-4 sm:mt-6 py-3 rounded-lg text-sm sm:text-base font-semibold flex items-center justify-center gap-2 transition ${
          !isFormValid || isProcessing || isOrderLoading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-brand-primary text-white hover:bg-red-700"
        }`}
        title={
          !isFormValid
            ? pendingReason ||
              "Preencha todos os campos obrigatórios para continuar"
            : isProcessing
            ? "Processando seu pedido..."
            : undefined
        }
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
            <span>Processando pedido...</span>
          </>
        ) : isOrderLoading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
            <span>Finalizando...</span>
          </>
        ) : (
          <>
            <DollarSign className="w-5 h-5" />
            Finalizar Pedido
          </>
        )}
      </button>
    </div>
  );
}
